/**
 * NCEMI-specific form validators.
 *
 * Validates NO_COST_EMI campaign-specific fields: funding shares, price leading
 * guardrail, and brand/share consistency. Extracted from formValidationUtils
 * for single-responsibility.
 *
 * @module ncemiValidation
 */

import { UseCampaignFormResult } from 'src/hooks/useCampaignForm/useCampaignForm';
import { parseOptionalNumber } from 'src/utils/helpers';
import { FieldErrors, ValidationMessage } from '../formValidationUtils';

// ============================================================================
// Public API
// ============================================================================

export function validateNcemi(form: UseCampaignFormResult, f: FieldErrors): void {
    validateFundingShares(form, f);
    validatePriceLeading(form, f);
    validateBrandShareConsistency(form, f);
}

// ============================================================================
// Internal validators
// ============================================================================

/** Validates each funding share is in [0, 100] and all four sum to 100. */
function validateFundingShares(form: UseCampaignFormResult, f: FieldErrors): void {
    const RANGE_MSG: ValidationMessage = { key: 'ncemi_funding_share_range' };
    const isOutOfRange = (s: number | null | undefined): boolean =>
        s != null && (isNaN(s) || s < 0 || s > 100);

    if (isOutOfRange(form.sellerShare)) f.sellerShare = RANGE_MSG;
    if (isOutOfRange(form.brandShare)) f.brandShare = RANGE_MSG;
    if (isOutOfRange(form.bankShare)) f.bankShare = RANGE_MSG;
    if (isOutOfRange(form.amazonShare)) f.amazonShare = RANGE_MSG;

    const allPresent = [form.sellerShare, form.brandShare, form.bankShare, form.amazonShare]
        .every((s) => s != null && !isNaN(s));
    if (allPresent) {
        const sum = (form.sellerShare ?? 0) + (form.brandShare ?? 0)
            + (form.bankShare ?? 0) + (form.amazonShare ?? 0);
        if (sum !== 100) {
            f.fundingSharesSum = { key: 'ncemi_funding_share_sum', params: { currentSum: sum } };
        }
    }
}

/** Validates the Amazon Price Leading Guardrail is between 3% and 100%. */
function validatePriceLeading(form: UseCampaignFormResult, f: FieldErrors): void {
    const priceLeading = parseOptionalNumber(form.priceLeadingGuardrail);
    if (priceLeading == null || priceLeading < 3 || priceLeading > 100) {
        f.priceLeadingGuardrail = { key: 'ncemi_price_leading_range' };
    }
}

/** Validates brand / brand-share consistency: brand required when share > 0 and vice-versa. */
function validateBrandShareConsistency(form: UseCampaignFormResult, f: FieldErrors): void {
    const brandShareVal = form.brandShare;
    const hasBrandSelected = !!form.brandName
        && form.brandName.trim().length > 0
        && form.brandName.trim() !== 'N/A';

    if (brandShareVal != null && !isNaN(brandShareVal) && brandShareVal > 0 && !hasBrandSelected) {
        f.brandName = { key: 'ncemi_brand_required_when_share' };
    }
    if (brandShareVal != null && !isNaN(brandShareVal) && brandShareVal === 0 && hasBrandSelected) {
        f.brandName = { key: 'ncemi_brand_share_required_when_brand' };
    }
}
